/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import * as React from "react";
import superjson from "superjson";
import * as api from "property-api/openapi";
import type { NextPage, GetServerSideProps } from "next";
import Link from "next/link";
import Layout from "../../components/Layout";
import { normalizeHeroImage } from "../../routes/images";
import client from "../../../client";
import {
	type GenericPageProps,
	getSiteSettings,
	pageQuery,
} from "../../routes/common";
import type { Page } from "../../types/generated/schema";
import { useState } from "react";
import BasicHero from "@components/sections/basic-hero/basic-hero";
import Container from "@components/container";
import { useRouter } from "next/router";
import { statusOptions } from "@/routes/properties";
import { Proposition } from "@components/proposition";
import { Pagination } from "@components/pagination";
import { debounce } from "ts-debounce";
import type { ApiOffersGet200Response } from "property-api/openapi";

type PageProps = GenericPageProps & {
	objects: ApiOffersGet200Response;
	allCategories: string[];
	allCities: string[];
	allStatusses: string[];
};

const AanbodPage: NextPage<PageProps> = ({
	objects,
	allCategories,
	allCities,
	allStatusses,
	pageData,
	config,
}) => {
	const { query, push } = useRouter();
	const [address, setAddress] = useState("");
	const addressInputRef = React.useRef(null);
	const [selectedCity, setSelectedCity] = useState(query.city || "all");
	const [selectedStatus, setSelectedStatus] = useState(query.status || "all");
	const [selectedCategory, setSelectedCategory] = useState(
		query.category || "all",
	);
	const [pageParams, setPageParams] = useState(new URLSearchParams());

	const data = superjson.parse<ApiOffersGet200Response>(String(objects));
	const hero = pageData.content.flatMap((sections) => {
		if (sections._type === "hero") {
			return sections.backgroundImageUrl;
		}

		return [];
	});

	const hasActiveFilters =
		query.city !== undefined ||
		query.status !== undefined ||
		query.category !== undefined ||
		address !== "";

	const handleCityChange = (e) => {
		setSelectedCity(e.target.value);
	};
	const handleStatusChange = (e) => {
		setSelectedStatus(e.target.value);
	};
	const handleCategoryChange = (e) => {
		setSelectedCategory(e.target.value);
	};

	function resetFilters(): void {
		setSelectedCity("all");
		setSelectedStatus("all");
		setSelectedCategory("all");
		setAddress("");

		if (addressInputRef !== null) {
			addressInputRef.current.value = "";
		}
	}

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	React.useEffect(() => {
		const params = new URLSearchParams();
		if (selectedCity.toString() !== "all") {
			params.set("city", selectedCity.toString());
		}
		if (selectedStatus.toString() !== "all") {
			params.set("status", selectedStatus.toString());
		}
		if (selectedCategory.toString() !== "all") {
			params.set("category", selectedCategory.toString());
		}
		if (address !== "") {
			params.set("address", address);
		}

		params.set("page", query.page?.toString() || "1");
		setPageParams(params);
		void push(
			`/aanbod${params.toString() !== "" ? `?${params.toString()}` : ""}`,
			undefined,
			{ scroll: false },
		);
	}, [address, selectedCity, selectedStatus, selectedCategory]);

	const onTextChange = (e) => {
		handleTextChange(e.target.value);
	};

	const handleTextChange = React.useCallback(
		debounce((e) => {
			setAddress(e);
		}, 500),
		[],
	);

	// There are 30 results per page, calculate the total pages
	const totalItemsPerPage = 9;
	const totalProperties = data.hydratotalItems ?? 1;
	const totalPages = Math.ceil(totalProperties / totalItemsPerPage);
	const currentPage = Math.round(
		Number.parseInt(query.page?.toString() || "1", 10),
	);

	return (
		<Layout config={config} tags={pageData.seo}>
			{hero[0] && (
				<BasicHero
					heading={
						<>
							Baron&apos;s <br /> aanbod
						</>
					}
					backgroundImageUrl={hero[0]}
				/>
			)}
			<Container>
				<form
					className="p-8 bg-gray-100 rounded-md"
					onSubmit={(e) => {
						e.preventDefault();
					}}
				>
					<div className="grid grid-cols-1 gap-6 filter-options sm:grid-cols-2 md:grid-cols-3">
						<div className="flex flex-col">
							<label
								htmlFor="address"
								className="block text-sm font-medium text-gray-700"
							>
								Adres
							</label>
							{/* eslint-disable-next-line jsx-a11y/no-onchange */}
							<input
								ref={addressInputRef}
								id="address"
								type="text"
								name="address"
								placeholder="Zoek op adres"
								className="block w-full px-4 py-2 text-sm border-gray-300 rounded-md shadow-md focus:outline-none focus:ring focus:ring-primary"
								onChange={onTextChange}
							/>
						</div>
						<div className="flex flex-col">
							<label
								htmlFor="location"
								className="block text-sm font-medium text-gray-700"
							>
								Plaats
							</label>
							{/* eslint-disable-next-line jsx-a11y/no-onchange */}
							<select
								id="location"
								name="city"
								className="relative w-full py-2 pl-3 pr-10 text-left bg-white rounded-lg shadow-md cursor-default focus-visible:border-indigo-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-primary sm:text-sm"
								value={selectedCity === null ? "all" : selectedCity}
								onChange={handleCityChange}
							>
								<option value="all">Alle plaatsen</option>
								{allCities.map((city, i) => (
									<option
										// biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
										key={`city-${i}`}
										value={city !== null ? city : "all"}
									>
										{city}
									</option>
								))}
							</select>
						</div>
						<div className="flex flex-col">
							<label
								htmlFor="property-category"
								className="block text-sm font-medium text-gray-700"
							>
								Categorie
							</label>
							{/* eslint-disable-next-line jsx-a11y/no-onchange */}
							<select
								id="property-category"
								name="category"
								className="relative w-full py-2 pl-3 pr-10 text-left bg-white rounded-lg shadow-md cursor-default focus-visible:border-indigo-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-primary sm:text-sm"
								value={selectedCategory === null ? "all" : selectedCategory}
								onChange={handleCategoryChange}
							>
								<option value="all">Alle categorieën</option>
								{allCategories.map((category, i) => (
									// biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
									<option key={`type-${i}`} value={category.toLowerCase()}>
										{category}
									</option>
								))}
							</select>
						</div>
						<div className="flex flex-col">
							<label
								htmlFor="status"
								className="block text-sm font-medium text-gray-700"
							>
								Status
							</label>
							{/* eslint-disable-next-line jsx-a11y/no-onchange */}
							<select
								id="status"
								name="status"
								className="relative w-full py-2 pl-3 pr-10 text-left bg-white rounded-lg shadow-md cursor-default focus-visible:border-indigo-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-primary sm:text-sm"
								value={selectedStatus === null ? "all" : selectedStatus}
								onChange={handleStatusChange}
							>
								<option value="all">Status</option>
								{allStatusses.map(
									(status, i) =>
										statusOptions[status] && (
											// biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
											<option key={`status-${i}`} value={status}>
												{statusOptions[status] || "Onbekend"}
											</option>
										),
								)}
							</select>
						</div>
						<div className="flex flex-col justify-end">
							<Link
								href={`/aanbod/?${pageParams.toString()}`}
								className="flex flex-row items-center flex-shrink-0 px-4 pt-3 pb-2 text-sm font-bold text-black uppercase rounded font-rigBold bg-primary"
							>
								{/* biome-ignore lint/a11y/useValidAnchor: next 12 */}
								Zoeken
							</Link>
						</div>
						{hasActiveFilters && (
							<div className="flex flex-col justify-end">
								<Link
									href="/aanbod"
									scroll={false}
									className="flex flex-row items-center flex-shrink-0 px-4 pt-3 pb-2 text-sm font-bold text-white uppercase bg-black rounded font-rigBold"
									// biome-ignore lint/a11y/useValidAnchor: next 12
									onClick={() => resetFilters()}
								>
									Wis filters
								</Link>
							</div>
						)}
					</div>
				</form>

				{data && data.hydramember.length > 0 ? (
					<div className="grid grid-cols-1 gap-4 my-6 text-sm md:grid-cols-2 lg:grid-cols-3">
						{data.hydramember.map((proposition) => (
							<Proposition key={proposition.slug} {...proposition} />
						))}
					</div>
				) : (
					<div className="grid grid-cols-1 gap-4 my-6 text-sm lg:grid-cols-3">
						<div className="flex flex-col p-6 space-y-2 bg-lightGray">
							<h4 className="font-bold text-black uppercase text-md">
								Geen items gevonden
							</h4>
						</div>
					</div>
				)}
				{data && totalPages > 0 && (
					<Pagination
						baseHref="/aanbod"
						accessibilityLabel="paginatie"
						currentPage={currentPage}
						pageCount={totalPages}
					/>
				)}
			</Container>
		</Layout>
	);
};

export default AanbodPage;

type FiltersQuery = {
	filters: {
		categories: string[];
		cities: string[];
		statusses: string[];
		types: string[];
	};
};

export const getServerSideProps: GetServerSideProps = async (props) => {
	const apiConfig = new api.Configuration({
		basePath: new URL(process.env.NEXT_PUBLIC_PROPERTY_API).origin,
	});
	const q = new api.OfferApi(apiConfig);

	const pageData = await client
		.fetch<Page>(pageQuery, { slug: "aanbod" })
		.then((res) => ({ ...res }));

	const allFilters: FiltersQuery = await (
		await fetch(
			`${
				new URL(process.env.NEXT_PUBLIC_PROPERTY_API).origin
			}/properties/overview-filters`,
		)
	).json();

	const objects = await q.apiOffersGet({
		archived: false,
		page: Number.parseInt(props.query?.page?.toString() || "1", 10),
		city:
			props.query?.city?.toString() !== "all"
				? props.query?.city?.toString()
				: undefined,
		title:
			props.query?.address?.toString() !== ""
				? props.query?.address?.toString()
				: undefined,
		status:
			props.query?.status?.toString() !== ""
				? props.query?.status?.toString()
				: undefined,
		category:
			props.query?.category?.toString() !== "all"
				? props.query?.category?.toString()
				: undefined,
	});

	const config = await getSiteSettings();

	if (!pageData || !config) {
		return {
			notFound: true,
		};
	}

	return {
		props: {
			pageData: normalizeHeroImage(pageData),
			objects: superjson.stringify(objects),
			allCities: allFilters.filters.cities,
			allStatusses: allFilters.filters.statusses,
			allCategories: allFilters.filters.categories,
			config,
		},
		// revalidate: 1800
	};
};
